<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { Check, Trash2 } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

// ── Props ─────────────────────────────────────────────────────────────

const props = defineProps<NodeViewProps>();

// ── Live footnote number ──────────────────────────────────────────────
// Scan the document for all footnoteRef nodes and find our position.

const footnoteNumber = computed<number>(() => {
    if (!props.editor) {
        return 0;
    }

    let n = 0;
    const myId = props.node.attrs.footnoteId as string;
    let found = 0;
    props.editor.state.doc.descendants((node) => {
        if (node.type.name === 'footnoteRef') {
            n++;

            if (node.attrs.footnoteId === myId) {
                found = n;

                return false;
            }
        }

        return true;
    });

    return found || n + 1;
});

// ── Popover editing ───────────────────────────────────────────────────

const open = ref(false);
const draft = ref<string>(props.node.attrs.content ?? '');
const textareaRef = ref<HTMLTextAreaElement | null>(null);

watch(open, (v) => {
    if (v) {
        draft.value = props.node.attrs.content ?? '';
        nextTick(() => textareaRef.value?.focus());
    }
});

watch(
    () => props.node.attrs.content as string,
    (v) => {
        if (!open.value) {
            draft.value = v;
        }
    },
);

function save() {
    props.updateAttributes({ content: draft.value });
    open.value = false;
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        save();
    }

    if (e.key === 'Escape') {
        open.value = false;
    }
}
</script>

<template>
    <NodeViewWrapper
        as="sup"
        class="footnote-ref inline cursor-pointer select-none"
        contenteditable="false"
    >
        <Popover v-model:open="open">
            <PopoverTrigger as-child>
                <button
                    class="rounded px-0.5 text-xs font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary/80"
                    :title="props.node.attrs.content"
                >
                    [{{ footnoteNumber }}]
                </button>
            </PopoverTrigger>
            <PopoverContent class="w-72 space-y-2 p-3" side="top" align="start">
                <p class="text-xs font-medium text-foreground">
                    Note de bas de page {{ footnoteNumber }}
                </p>
                <Textarea
                    ref="textareaRef"
                    v-model="draft"
                    :rows="3"
                    class="text-sm"
                    placeholder="Contenu de la note…"
                    @keydown="onKeydown"
                />
                <div class="flex items-center gap-1">
                    <Button size="sm" class="h-7 px-3" @click="save">
                        <Check class="mr-1 h-3.5 w-3.5" />
                        Enregistrer
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        class="ml-auto h-7 px-2 text-destructive hover:text-destructive"
                        @click="props.deleteNode()"
                    >
                        <Trash2 class="h-3.5 w-3.5" />
                    </Button>
                </div>
                <p class="text-[11px] text-muted-foreground">
                    Ctrl+Entrée pour enregistrer
                </p>
            </PopoverContent>
        </Popover>
    </NodeViewWrapper>
</template>
